import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Send, Shield } from 'lucide-react';

export default function DoctorChat() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [input, setInput] = useState('');
  const [ws, setWs] = useState(null);
  const scrollRef = useRef(null);
  const token = localStorage.getItem('ss_token');

  useEffect(() => {
    api.get(`/api/consultations/${sessionId}`).then(res => setSession(res.data));
    
    // Connect WebSocket
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace('https://', 'wss://').replace('http://', 'ws://');
    const wsUrl = `${backendUrl}/ws/consultation/${sessionId}?token=${token}`;
    try {
      const socket = new WebSocket(wsUrl);
      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'message') {
          setSession(prev => prev ? {...prev, messages: [...(prev.messages || []), data.data]} : prev);
        }
      };
      socket.onclose = () => console.log('WS closed');
      setWs(socket);
      return () => socket.close();
    } catch { 
      // Fallback to polling
      const interval = setInterval(() => {
        api.get(`/api/consultations/${sessionId}`).then(res => setSession(res.data));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session?.messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ content: input }));
    } else {
      await api.post(`/api/consultations/${sessionId}/message`, { content: input });
      const res = await api.get(`/api/consultations/${sessionId}`);
      setSession(res.data);
    }
    setInput('');
  };

  if (!session) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h2 className="font-heading text-xl font-semibold">{session.learner_name || 'Patient'}</h2>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800">{session.status}</Badge>
        </div>
        {session.ai_intake_summary && (
          <div className="max-w-md">
            <p className="text-xs text-muted-foreground"><Shield className="h-3 w-3 inline mr-1" />AI Summary: {session.ai_intake_summary.slice(0, 150)}...</p>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin" ref={scrollRef}>
        {(session.messages || []).map((msg, i) => (
          <div key={i} className={`flex ${msg.sender_type === 'doctor' ? 'justify-end' : msg.sender_type === 'system' ? 'justify-center' : 'justify-start'}`}>
            {msg.sender_type === 'system' ? (
              <p className="text-xs text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">{msg.content}</p>
            ) : (
              <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${msg.sender_type === 'doctor' ? 'bg-primary text-white rounded-br-md' : 'bg-muted rounded-bl-md'}`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.had_pii_blocked && <p className="text-[10px] opacity-60 mt-1">Some contact info was removed for safety.</p>}
                <p className="text-[10px] opacity-50 mt-0.5">{new Date(msg.timestamp).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input */}
      {session.status === 'active' && (
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}} placeholder="Type your message..." className="min-h-[44px] max-h-[100px] resize-none" />
            <Button size="icon" onClick={sendMessage} disabled={!input.trim()}><Send className="h-4 w-4" /></Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Personal contact info is automatically filtered for safety.</p>
        </div>
      )}
    </div>
  );
}
